import React, { useState } from "react";
import { 
  FiPlus, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiThumbsUp, 
  FiTrash2, 
  FiAward, 
  FiEye,
  FiFilter
} from "react-icons/fi";
import type { 
  FishboneDiagram, 
  FishboneCategory, 
  FishboneCause, 
  RcaCategory6M 
} from "../../../../types/rca.types.js";

export type FishboneWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  initialCategories?: FishboneCategory[] | undefined;
  readOnly?: boolean;
  onSave?: (diagram: FishboneDiagram) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const INITIAL_CATEGORIES: FishboneCategory[] = [
  {
    id: "cat-man",
    code: "MAN",
    name: "Man (People)",
    category6M: "MAN",
    causes: [
      { id: "c-man-1", categoryId: "cat-man", causeDescription: "Operator visual fatigue during 3rd shift", level: 1, isRootCause: false, votesCount: 2, verificationStatus: "VERIFIED" },
      { id: "c-man-2", categoryId: "cat-man", causeDescription: "Training gap on new CNC offset tool", level: 1, isRootCause: false, votesCount: 4, verificationStatus: "NOT_VERIFIED" },
      { id: "c-man-3", categoryId: "cat-man", causeDescription: "Inconsistent handover between shifts", level: 1, isRootCause: false, votesCount: 1, verificationStatus: "NOT_VERIFIED" },
    ]
  },
  {
    id: "cat-machine",
    code: "MACHINE",
    name: "Machine",
    category6M: "MACHINE",
    causes: [
      { id: "c-mac-1", categoryId: "cat-machine", causeDescription: "Calibration drift on Z-axis spindle", level: 1, isRootCause: true, rootCauseJustification: "Laser interferometer verified 0.08mm deviation beyond compensation envelope.", votesCount: 7, verificationStatus: "VERIFIED" },
      { id: "c-mac-2", categoryId: "cat-machine", causeDescription: "Coolant nozzle pressure fluctuation", level: 1, isRootCause: false, votesCount: 1, verificationStatus: "REJECTED" },
      { id: "c-mac-3", categoryId: "cat-machine", causeDescription: "Fixture clamp wear & play", level: 1, isRootCause: false, votesCount: 3, verificationStatus: "VERIFIED" },
    ]
  },
  {
    id: "cat-method",
    code: "METHOD",
    name: "Method",
    category6M: "METHOD",
    causes: [
      { id: "c-met-1", categoryId: "cat-method", causeDescription: "Milling feed rate setting too aggressive", level: 1, isRootCause: false, votesCount: 2, verificationStatus: "VERIFIED" },
      { id: "c-met-2", categoryId: "cat-method", causeDescription: "First-piece inspection protocol skipped", level: 1, isRootCause: false, votesCount: 5, verificationStatus: "NOT_VERIFIED" },
    ]
  },
  {
    id: "cat-material",
    code: "MATERIAL",
    name: "Material",
    category6M: "MATERIAL",
    causes: [
      { id: "c-mat-1", categoryId: "cat-material", causeDescription: "Raw forging alloy hardness variation", level: 1, isRootCause: false, votesCount: 1, verificationStatus: "REJECTED" },
      { id: "c-mat-2", categoryId: "cat-material", causeDescription: "Thermal expansion in raw stock storage", level: 1, isRootCause: false, votesCount: 0, verificationStatus: "NOT_VERIFIED" },
    ]
  },
  {
    id: "cat-measurement",
    code: "MEASUREMENT",
    name: "Measurement",
    category6M: "MEASUREMENT",
    causes: [
      { id: "c-mea-1", categoryId: "cat-measurement", causeDescription: "Micrometer zero-point not re-zeroed", level: 1, isRootCause: false, votesCount: 2, verificationStatus: "NOT_VERIFIED" },
      { id: "c-mea-2", categoryId: "cat-measurement", causeDescription: "Gauge repeatability & reproducibility (GR&R) high", level: 1, isRootCause: false, votesCount: 3, verificationStatus: "VERIFIED" },
    ]
  },
  {
    id: "cat-environment",
    code: "ENVIRONMENT",
    name: "Environment",
    category6M: "ENVIRONMENT",
    causes: [
      { id: "c-env-1", categoryId: "cat-environment", causeDescription: "Ambient shop floor temperature spike (>32°C)", level: 1, isRootCause: false, votesCount: 4, verificationStatus: "VERIFIED" },
      { id: "c-env-2", categoryId: "cat-environment", causeDescription: "High seasonal humidity affecting coolant concentration", level: 1, isRootCause: false, votesCount: 1, verificationStatus: "NOT_VERIFIED" },
    ]
  },
];

export function FishboneWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  initialCategories,
  readOnly = false,
  onSave,
  onRootCauseIdentified,
}: FishboneWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"diagram" | "causes">("diagram");
  const [categories, setCategories] = useState<FishboneCategory[]>(
    initialCategories && initialCategories.length > 0 ? initialCategories : INITIAL_CATEGORIES,
  );
  const [selectedCategory, setSelectedCategory] = useState<string>("cat-machine");
  const [newCauseText, setNewCauseText] = useState<string>("");
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [saveToast, setSaveToast] = useState<boolean>(false);

  React.useEffect(() => {
    if (initialCategories && initialCategories.length > 0) {
      setCategories(initialCategories);
    }
  }, [initialCategories]);

  const totalCauses = categories.reduce((sum, c) => sum + c.causes.length, 0);
  const shortlistedCauses = categories.reduce(
    (sum, c) => sum + c.causes.filter(cause => (cause.votesCount ?? 0) >= 3 || cause.isRootCause).length, 
    0
  );

  const handleAddCause = () => {
    if (!newCauseText.trim() || readOnly) return;
    const newCause: FishboneCause = {
      id: `cause-${Date.now()}`,
      categoryId: selectedCategory,
      causeDescription: newCauseText.trim(),
      level: 1,
      isRootCause: false,
      votesCount: 0,
      verificationStatus: "NOT_VERIFIED",
    };

    setCategories(prev => prev.map(cat => {
      if (cat.id === selectedCategory) {
        return { ...cat, causes: [...cat.causes, newCause] };
      }
      return cat;
    }));

    setNewCauseText("");
    setShowAddModal(false);
  };

  const handleVote = (causeId: string) => {
    if (readOnly) return;
    setCategories(prev => prev.map(cat => ({
      ...cat,
      causes: cat.causes.map(c => c.id === causeId ? { ...c, votesCount: (c.votesCount ?? 0) + 1 } : c)
    })));
  };

  const handleToggleRootCause = (causeId: string, categoryCode: string) => {
    if (readOnly) return;
    let verifiedText = "";
    let verifiedCat = categoryCode;
    setCategories(prev => prev.map(cat => ({
      ...cat,
      causes: cat.causes.map(c => {
        if (c.id === causeId) {
          const nextState = !c.isRootCause;
          if (nextState) {
            verifiedText = c.causeDescription;
            verifiedCat = cat.code;
          }
          return { 
            ...c, 
            isRootCause: nextState, 
            verificationStatus: nextState ? ("VERIFIED" as const) : (c.verificationStatus || ("NOT_VERIFIED" as const)) 
          };
        }
        return c;
      })
    })));

    if (verifiedText) {
      onRootCauseIdentified?.(verifiedText, verifiedCat, 90);
    }
  };

  const handleDeleteCause = (causeId: string) => {
    if (readOnly) return;
    setCategories(prev => prev.map(cat => ({
      ...cat,
      causes: cat.causes.filter(c => c.id !== causeId)
    })));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            FB
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              12. Fishbone Analysis Workspace (Ishikawa)
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                6M Framework
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Categorize potential causes of problems to identify their root causes across 6 industrial dimensions.
            </p>
          </div>
        </div>

        {/* Header Actions */}
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab("diagram")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "diagram" 
                  ? "bg-white text-indigo-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Diagram
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("causes")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "causes" 
                  ? "bg-white text-indigo-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Causes Matrix ({totalCauses})
            </button>
          </div>

          {!readOnly && (
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="px-3.5 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
            >
              <FiPlus className="w-3.5 h-3.5" />
              Add Cause
            </button>
          )}

          {!readOnly && onSave && (
            <button
              type="button"
              onClick={() => {
                const rootCauseItem = categories.flatMap(c => c.causes).find(cause => cause.isRootCause);
                const totalCauses = categories.reduce((sum, c) => sum + c.causes.length, 0);
                onSave({
                  id: `fb-${Date.now()}`,
                  rcaRecordId,
                  diagramTitle: "Fishbone Analysis Diagram",
                  problemStatement,
                  framework: "6M",
                  categories,
                  totalCausesCount: totalCauses,
                  rootCausesCount: rootCauseItem ? 1 : 0,
                  votingEnabled: false,
                  status: rootCauseItem ? "COMPLETED" : "IN_PROGRESS",
                });
                setSaveToast(true);
                setTimeout(() => setSaveToast(false), 3500);
              }}
              className="px-3.5 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
            >
              <FiAward className="w-3.5 h-3.5" />
              Save Analysis
            </button>
          )}
        </div>
      </div>

      {saveToast && (
        <div className="px-6 py-2.5 bg-emerald-100 text-emerald-900 text-xs font-semibold flex items-center gap-2 border-b border-emerald-300">
          <FiCheckCircle className="w-4 h-4 text-emerald-700" />
          Fishbone Analysis successfully saved to database.
        </div>
      )}

      {/* Metrics Strip */}
      <div className="px-6 py-2.5 bg-indigo-50/40 border-b border-indigo-100 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-slate-500">Total Causes: </span>
            <span className="font-bold text-slate-800">{totalCauses}</span>
          </div>
          <div>
            <span className="text-slate-500">Shortlisted: </span>
            <span className="font-bold text-indigo-700">{shortlistedCauses}</span>
          </div>
          <div>
            <span className="text-slate-500">Root Cause Candidates: </span>
            <span className="font-bold text-rose-600">
              {categories.reduce((s, c) => s + c.causes.filter(x => x.isRootCause).length, 0)}
            </span>
          </div>
        </div>

        <div className="text-[11px] text-slate-500 flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500"></span>
          <span>Red highlight indicates verified / candidate Root Cause</span>
        </div>
      </div>

      {/* Diagram Canvas or Matrix */}
      <div className="p-6">
        {activeTab === "diagram" ? (
          <div className="relative bg-slate-50/70 rounded-xl border border-slate-200 p-6 overflow-x-auto min-w-[850px]">
            {/* 6M Fishbone Interactive Canvas */}
            <div className="grid grid-cols-3 gap-6 relative">
              {/* Top 3 Categories: Man, Machine, Method */}
              {categories.slice(0, 3).map(cat => (
                <div key={cat.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-xs font-bold text-indigo-700">{cat.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                      {cat.causes.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {cat.causes.map(c => (
                      <div
                        key={c.id}
                        className={`p-2 rounded-lg text-xs border transition-all ${
                          c.isRootCause 
                            ? "bg-rose-50 border-rose-300 text-rose-900 shadow-sm" 
                            : "bg-slate-50 border-slate-200 text-slate-800 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <span className="font-medium text-[11px] leading-tight">{c.causeDescription}</span>
                          {c.isRootCause && (
                            <FiAward className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                          )}
                        </div>
                        <div className="mt-1.5 pt-1 border-t border-slate-200 flex items-center justify-between text-[10px]">
                          <button
                            type="button"
                            onClick={() => handleVote(c.id)}
                            disabled={readOnly}
                            className="flex items-center gap-1 text-indigo-600 font-semibold hover:underline"
                          >
                            <FiThumbsUp className="w-2.5 h-2.5" /> {c.votesCount ?? 0} votes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggleRootCause(c.id, cat.code)}
                            disabled={readOnly}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                              c.isRootCause ? "bg-rose-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-rose-100 hover:text-rose-700"
                            }`}
                          >
                            {c.isRootCause ? "Root Cause" : "Mark Root"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Central Fishbone Spine with Effect Box */}
            <div className="my-6 flex items-center gap-0">
              <div className="flex-1 h-2 bg-gradient-to-r from-slate-400 via-indigo-600 to-indigo-800 dark:from-slate-600 dark:via-indigo-500 dark:to-indigo-400 rounded-l-full"></div>
              {/* Fish Head / Problem Effect */}
              <div className="bg-rose-600 text-white px-5 py-3 rounded-r-2xl rounded-l-lg shadow-md max-w-xs text-right shrink-0 border-2 border-rose-500">
                <div className="text-[10px] uppercase font-bold tracking-wider text-rose-200">Problem Effect</div>
                <div className="text-xs font-bold leading-tight mt-0.5">
                  {problemStatement || "Product Dimension Out of Tolerance"}
                </div>
              </div>
            </div>

            {/* Bottom 3 Categories: Material, Measurement, Environment */}
            <div className="grid grid-cols-3 gap-6">
              {categories.slice(3, 6).map(cat => (
                <div key={cat.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-xs font-bold text-indigo-700">{cat.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                      {cat.causes.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {cat.causes.map(c => (
                      <div
                        key={c.id}
                        className={`p-2 rounded-lg text-xs border transition-all ${
                          c.isRootCause 
                            ? "bg-rose-50 border-rose-300 text-rose-900 shadow-sm" 
                            : "bg-slate-50 border-slate-200 text-slate-800 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <span className="font-medium text-[11px] leading-tight">{c.causeDescription}</span>
                          {c.isRootCause && (
                            <FiAward className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                          )}
                        </div>
                        <div className="mt-1.5 pt-1 border-t border-slate-200 flex items-center justify-between text-[10px]">
                          <button
                            type="button"
                            onClick={() => handleVote(c.id)}
                            disabled={readOnly}
                            className="flex items-center gap-1 text-indigo-600 font-semibold hover:underline"
                          >
                            <FiThumbsUp className="w-2.5 h-2.5" /> {c.votesCount ?? 0} votes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggleRootCause(c.id, cat.code)}
                            disabled={readOnly}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                              c.isRootCause ? "bg-rose-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-rose-100 hover:text-rose-700"
                            }`}
                          >
                            {c.isRootCause ? "Root Cause" : "Mark Root"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Causes Matrix Table */
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border border-slate-200 rounded-lg">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">6M Category</th>
                  <th className="py-2.5 px-3">Cause Hypothesis</th>
                  <th className="py-2.5 px-3">Votes</th>
                  <th className="py-2.5 px-3">Verification Status</th>
                  <th className="py-2.5 px-3">Root Cause?</th>
                  <th className="py-2.5 px-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {categories.flatMap(cat => cat.causes.map(c => (
                  <tr key={c.id} className={c.isRootCause ? "bg-rose-50 font-medium" : ""}>
                    <td className="py-2.5 px-3 font-semibold text-indigo-700">{cat.name}</td>
                    <td className="py-2.5 px-3 text-slate-900 font-medium">{c.causeDescription}</td>
                    <td className="py-2.5 px-3">{c.votesCount ?? 0}</td>
                    <td className="py-2.5 px-3">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                        c.verificationStatus === "VERIFIED" ? "bg-emerald-100 text-emerald-800" :
                        c.verificationStatus === "REJECTED" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-800"
                      }`}>
                        {c.verificationStatus || "NOT_VERIFIED"}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      {c.isRootCause ? (
                        <span className="text-rose-600 font-bold flex items-center gap-1">
                          <FiCheckCircle className="w-3.5 h-3.5" /> YES
                        </span>
                      ) : "No"}
                    </td>
                    <td className="py-2.5 px-3">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => handleDeleteCause(c.id)}
                          className="text-slate-400 hover:text-rose-600 transition-colors"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Cause Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl max-w-md w-full p-5 space-y-4">
            <h3 className="text-sm font-bold text-slate-900">Add Fishbone Cause</h3>
            
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Select 6M Category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as RcaCategory6M)}
                className="w-full text-xs p-2 rounded-lg border border-slate-300 bg-white text-slate-800"
              >
                {categories.map(cat => (
                  <option key={cat.code} value={cat.code}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Cause Hypothesis:</label>
              <textarea
                rows={3}
                value={newCauseText}
                onChange={(e) => setNewCauseText(e.target.value)}
                placeholder="State the potential failure cause hypothesis..."
                className="w-full text-xs p-2 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-1 focus:ring-indigo-500 font-medium"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-3.5 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddCause}
                className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold shadow-sm"
              >
                Add Cause
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
